import { useExperienceApi } from '@/shared/api/useExperienceApi.ts';
import { useQuery } from '@tanstack/vue-query';
import { getBootstrapData } from '@experience/core-hostapp-share-lib';
import type { GetExperiencesData } from '@/shared/api/experience';
import type { Ref, MaybeRef } from 'vue';
import { useDisplayCurrencyQueryParams } from '@/shared/composables/useDisplayCurrencyQueryParams.ts';

export const useGetExperiencesPopular = (
  params: Ref<GetExperiencesData['query']>,
  enabled: MaybeRef<boolean> = true,
) => {
  const { getExperiences } = useExperienceApi();
  const displayCurrencyQueryParams = useDisplayCurrencyQueryParams();

  const excludeNeedsLicense = computed(() => {
    return getBootstrapData().hideUnlicensedExperiencesForPartner;
  });

  const popularQueryKeyParams = computed(() => ({
    ...params.value,
    ...displayCurrencyQueryParams.value,
  }));

  const {
    data: experiencesData,
    isFetching,
    isFetched,
  } = useQuery({
    queryKey: computed(() => ['getExperiencesList', popularQueryKeyParams.value]),
    queryFn: async () => {
      const response = await getExperiences({
        query: {
          ...popularQueryKeyParams.value,
          limit: 0,
          offset: 0,
          extended_movement_types: true,
          exclude_needs_license: excludeNeedsLicense.value,
          price_format: 'travelers' as const,
          photos_count: 1,
        },
      });

      return response.data;
    },
    enabled,
    placeholderData: (prevData, previousQuery) => {
      const prevParams = previousQuery?.queryKey?.[1] as { display_currency?: string } | undefined;
      const nextCurrency = popularQueryKeyParams.value.display_currency;

      if (prevParams?.display_currency !== nextCurrency) {
        return undefined;
      }

      return prevData ?? { count: 0, next: null, previous: null, results: [] };
    },
    refetchOnWindowFocus: false,
    retry: false,
  });

  return {
    experiencesData,
    isFetching,
    isFetched,
  };
};
